/**
 * EditPlaylistModal Component
 * Modal for editing a playlist's name and prompt with reset/keep options
 */

import { useState } from 'react';
import { updatePlaylist, type Playlist, type UpdatePlaylistMode } from '../api';
import ModalPortal from './ModalPortal';

interface Props {
  playlist: Playlist;
  movieCount: number;
  onClose: () => void;
  onUpdated: (playlist: Playlist) => void;
}

export default function EditPlaylistModal({ playlist, movieCount, onClose, onUpdated }: Props) {
  const [name, setName] = useState(playlist.name);
  const [prompt, setPrompt] = useState(playlist.prompt || '');
  const [mode, setMode] = useState<UpdatePlaylistMode>('keep');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPromptChanged = prompt.trim() !== (playlist.prompt || '').trim();
  const hasNameChanged = name.trim() !== playlist.name.trim();
  const hasChanges = hasPromptChanged || hasNameChanged;

  // Show mode options only if prompt changed and there are existing movies
  const showModeOptions = hasPromptChanged && movieCount > 0 && prompt.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedPrompt = prompt.trim();

    // Validation: need at least a name if removing prompt
    if (!trimmedPrompt && !trimmedName) {
      setError('Name is required when removing the AI prompt');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedPlaylist = await updatePlaylist(playlist.id, {
        name: hasNameChanged ? trimmedName : undefined,
        prompt: hasPromptChanged ? trimmedPrompt : undefined,
        mode: hasPromptChanged && trimmedPrompt ? mode : undefined,
      });
      onUpdated(updatedPlaylist);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update playlist');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Playlist</h2>
            <button className="modal-close" onClick={handleClose} disabled={loading}>
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="edit-playlist-name">Playlist Name</label>
              <input
                id="edit-playlist-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My Favorite Spy Movies"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-playlist-prompt">AI Prompt (optional)</label>
              <textarea
                id="edit-playlist-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Leave empty for a manual playlist, or describe the movies you want"
                rows={4}
                disabled={loading}
              />
              <p className="form-hint">
                Changing the prompt will search for new movies matching your criteria.
              </p>
            </div>

            {showModeOptions && (
              <div className="form-group">
                <label>What should happen to existing movies?</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="update-mode"
                      value="keep"
                      checked={mode === 'keep'}
                      onChange={() => setMode('keep')}
                      disabled={loading}
                    />
                    <span className="radio-text">
                      <strong>Keep existing movies</strong>
                      <span className="radio-description">
                        Add new movies that match, keep the {movieCount} current movies
                      </span>
                    </span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="update-mode"
                      value="reset"
                      checked={mode === 'reset'}
                      onChange={() => setMode('reset')}
                      disabled={loading}
                    />
                    <span className="radio-text">
                      <strong>Reset and replace</strong>
                      <span className="radio-description">
                        Remove all current movies and start fresh with the new prompt
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {mode === 'reset' && showModeOptions && (
              <div className="form-warning">
                Warning: This will remove all {movieCount} movies from your playlist and search
                again.
              </div>
            )}

            {error && <div className="form-error">{error}</div>}

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" disabled={loading || !hasChanges}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
